import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { 
  Brain, Zap, BookOpen, Gamepad2, Trophy, TrendingUp, 
  ChevronRight, Play, Settings, LogOut, User, Clock,
  Flame, Target, Award, MessageSquare, Map, Sparkles, Crown,
  Newspaper, Users, Bell, Search, ShieldAlert, Heart,
  Gift, Link2, Star, Share2, Radio, Lightbulb, Calendar,
  GraduationCap, UserPlus, BarChart3, Lock, Bitcoin, Youtube
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ShareCardButton } from "../components/ShareCard";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [btcPrice, setBtcPrice] = useState(null);
  const [fearGreed, setFearGreed] = useState(null);
  const [mempool, setMempool] = useState(null);
  const [halving, setHalving] = useState(null);
  const [rewardStats, setRewardStats] = useState(null);
  const [brainScore, setBrainScore] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [priceRes, fearRes, mempoolRes, halvingRes, rewardsRes, sectionsRes, scoreRes] = await Promise.all([
          api.get("/widgets/bitcoin-price"),
          api.get("/widgets/fear-greed"),
          api.get("/widgets/mempool"),
          api.get("/widgets/halving-countdown"),
          api.get("/rewards/stats"),
          api.get("/course/sections"),
          api.get("/achievements/score")
        ]);
        
        setBtcPrice(priceRes.data);
        setFearGreed(fearRes.data);
        setMempool(mempoolRes.data);
        setHalving(halvingRes.data);
        setRewardStats(rewardsRes.data);
        setSections(sectionsRes.data.sections);
        setBrainScore(scoreRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    refreshUser();

    // Refresh price every 60 seconds
    const interval = setInterval(async () => {
      try {
        const res = await api.get("/widgets/bitcoin-price");
        setBtcPrice(res.data);
      } catch {}
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshUser]);

  const getFearGreedColor = (value) => {
    if (value <= 25) return "text-red-500";
    if (value <= 45) return "text-orange-500";
    if (value <= 55) return "text-yellow-500";
    if (value <= 75) return "text-green-400";
    return "text-emerald-400";
  };

  const completedSections = user?.sections_completed?.length || 0;
  const totalSections = 21;
  const courseProgress = (completedSections / totalSections) * 100;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-10 h-10 rounded-lg" />
              <span className="font-heading text-xl font-bold text-white hidden sm:block">
                BITCOIN <span className="text-[#F7931A]">BRAIN</span>
              </span>
            </Link>
            
            <div className="flex items-center gap-4">
              {/* BTC XP Display */}
              <div className="flex items-center gap-2 bg-[#F7931A]/10 px-3 py-1.5 rounded-lg border border-[#F7931A]/30">
                <Zap className="text-[#F7931A]" size={16} />
                <span className="font-mono text-[#F7931A] font-medium" data-testid="xp-display">
                  {(user?.total_lifetime_xp || 0).toLocaleString()} BTC XP
                </span>
              </div>
              
              <Link to="/profile" className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                <User className="text-zinc-400" size={20} />
              </Link>
              
              <button onClick={handleLogout} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                <LogOut className="text-zinc-400" size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white">
            Welcome back, <span className="text-[#F7931A]">{user?.first_name}</span>
          </h1>
          <p className="text-zinc-500 mt-1">Continue your Bitcoin education journey</p>
        </div>

        {/* Email Verification Banner */}
        {!user?.email_verified && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-xl p-4 flex items-center gap-3"
            data-testid="email-verify-banner"
          >
            <ShieldAlert className="text-[#F7931A] flex-shrink-0" size={20} />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Verify your email address</p>
              <p className="text-zinc-500 text-xs">Check your inbox for a verification link to unlock the full experience.</p>
            </div>
            <Button
              size="sm"
              className="btn-primary text-xs h-7"
              onClick={async () => {
                try {
                  await api.post("/auth/resend-verification");
                  toast.success("Verification email sent!");
                } catch {
                  toast.error("Failed to send verification email");
                }
              }}
              data-testid="resend-verify-btn"
            >
              Resend
            </Button>
          </motion.div>
        )}

        {/* Educator welcome banner */}
        {user?.purchase_tier === "educator" && !localStorage.getItem(`educator_welcome_${user?.id}`) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-xl p-4 flex items-center gap-3"
            data-testid="educator-welcome-banner"
          >
            <Crown className="text-[#F7931A] flex-shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">All 21 sections are unlocked</p>
              <p className="text-zinc-500 text-xs">We recommend going through the course in order for the best learning experience.</p>
            </div>
            <button 
              onClick={() => { localStorage.setItem(`educator_welcome_${user?.id}`, 'true'); window.location.reload(); }}
              className="text-zinc-500 hover:text-white text-xs underline"
            >
              Got it
            </button>
          </motion.div>
        )}

        {/* Free tier upgrade banner */}
        {user?.purchase_tier === "free" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-xl p-4 flex items-center gap-3"
            data-testid="free-tier-banner"
          >
            <Lock className="text-[#F7931A] flex-shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">Free account — Section 1 + Kid Mode unlocked</p>
              <p className="text-zinc-500 text-xs">Upgrade to access all 21 sections, Weekly Briefs, Quiz Arena, Rabbit Hole, and more</p>
            </div>
            <Button size="sm" className="btn-primary text-xs h-7" onClick={() => navigate("/pricing")} data-testid="free-upgrade-btn">
              Upgrade
            </Button>
          </motion.div>
        )}

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Educator Hub Card — educator tier only */}
          {user?.purchase_tier === "educator" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:col-span-4 card-dark p-5 border-[#F7931A]/30 bg-gradient-to-r from-[#F7931A]/5 to-transparent"
              data-testid="educator-hub-card"
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                    <GraduationCap className="text-[#F7931A]" size={22} />
                  </div>
                  <div>
                    <p className="text-white font-heading font-bold">Educator Dashboard</p>
                    <p className="text-zinc-500 text-xs">Manage students, track progress, print certificates</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 bg-[#0A0A0A] px-3 py-1.5 rounded-lg border border-white/10">
                    <Users className="text-[#F7931A]" size={14} />
                    <span className="text-zinc-400 font-mono text-sm" data-testid="edu-student-count-dashboard">
                      Students linked
                    </span>
                  </div>
                  <Button
                    onClick={() => navigate("/educator")}
                    className="btn-primary text-sm"
                    data-testid="edu-hub-open-btn"
                  >
                    Open <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Admin Hub Card — admin only */}
          {user?.email === "bryce@bitcoinbrain.space" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:col-span-4 card-dark p-5 border-purple-500/30 bg-gradient-to-r from-purple-500/5 to-transparent cursor-pointer hover:border-purple-500/50 transition-all"
              onClick={() => navigate("/admin")}
              data-testid="admin-hub-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <BarChart3 className="text-purple-400" size={22} />
                  </div>
                  <div>
                    <p className="text-white font-heading font-bold">Admin Dashboard</p>
                    <p className="text-zinc-500 text-xs">Analytics, gift access, manage platform</p>
                  </div>
                </div>
                <ChevronRight className="text-zinc-500" size={16} />
              </div>
            </motion.div>
          )}

          {/* BTC XP Progress Card - Tall */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:row-span-2 card-dark p-6"
            data-testid="xp-rewards-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <Zap className="text-[#F7931A]" size={20} />
              <span className="text-zinc-400 text-sm font-medium">BTC XP</span>
            </div>
            
            <div className="text-center mb-4">
              <p className="font-mono text-4xl font-bold text-[#F7931A]" data-testid="appreciated-xp">
                {(rewardStats?.appreciated_xp || rewardStats?.total_lifetime_xp || user?.total_lifetime_xp || 0).toLocaleString()}
              </p>
              <p className="text-zinc-500 text-xs mt-1">
                Appreciated BTC XP
              </p>
              {rewardStats?.xp_growth_pct > 0 && (
                <p className="text-green-400 text-xs font-mono mt-0.5" data-testid="xp-growth">
                  +{rewardStats.xp_growth_pct}% ({rewardStats.days_hodled}d HODLed)
                </p>
              )}
            </div>

            {/* 21M Cap Progress */}
            <div className="bg-[#0A0A0A] rounded-lg p-3 mb-3" data-testid="xp-cap-progress">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-zinc-500">21M Cap</span>
                <span className="text-[#F7931A] font-mono">
                  {((rewardStats?.appreciated_xp || rewardStats?.total_lifetime_xp || 0) / 21000000 * 100).toFixed(4)}%
                </span>
              </div>
              <div className="w-full h-2 bg-[#161616] rounded-full overflow-hidden border border-white/5">
                <div 
                  className="h-full bg-gradient-to-r from-[#F7931A] to-[#F7931A]/60 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.max(0.5, (rewardStats?.appreciated_xp || rewardStats?.total_lifetime_xp || 0) / 21000000 * 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-zinc-700 font-mono">{((rewardStats?.appreciated_xp || rewardStats?.total_lifetime_xp || 0) / 1000).toFixed(1)}K</span>
                <span className="text-zinc-700 font-mono">21M</span>
              </div>
            </div>

            <div className="bg-[#0A0A0A] rounded-lg p-3 mb-4">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-zinc-600">Today</span>
                <span className="text-white font-mono">
                  +{(rewardStats?.xp_earned_today || 0).toLocaleString()} BTC XP
                </span>
              </div>
              <Progress 
                value={Math.min(100, ((rewardStats?.xp_earned_today || 0) / Math.max(1, rewardStats?.xp_earned_today || 100)) * 100)} 
                className="h-2"
              />
            </div>
            
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Base BTC XP</span>
                <span className="text-zinc-400 font-mono">{(rewardStats?.total_lifetime_xp || user?.total_lifetime_xp || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Level</span>
                <span className="text-[#F7931A]">{rewardStats?.xp_level || user?.xp_level || 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Title</span>
                <span className="text-white text-xs">{rewardStats?.xp_title || user?.xp_title || "Fiat Sleeper"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">Streak</span>
                <span className="flex items-center gap-1 text-orange-400">
                  <Flame size={14} />
                  {rewardStats?.streak_days || 0} days
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Appreciation</span>
                <span className="text-green-400 font-mono text-xs">{rewardStats?.multiplier || "1.0x"} bonus</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">CAGR</span>
                <span className="text-[#F7931A] font-mono text-xs">+107%/yr</span>
              </div>
            </div>
            <p className="text-zinc-600 text-[10px] mt-3 text-center italic">Matches Bitcoin's actual CAGR from 2011-2021</p>
          </motion.div>

          {/* BTC Price Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-dark p-6"
            data-testid="btc-price-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="text-[#F7931A]" size={18} />
              <span className="text-zinc-400 text-sm">BTC/USD</span>
            </div>
            <p className="font-mono text-3xl font-bold text-white">
              ${btcPrice?.price?.toLocaleString() || "—"}
            </p>
            <p className={`text-sm mt-1 ${btcPrice?.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {btcPrice?.change_24h >= 0 ? '+' : ''}{btcPrice?.change_24h?.toFixed(2)}% (24h)
            </p>
          </motion.div>

          {/* Fear & Greed Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-dark p-6"
            data-testid="fear-greed-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <Brain className="text-[#F7931A]" size={18} />
              <span className="text-zinc-400 text-sm">Fear & Greed</span>
            </div>
            <p className={`font-mono text-3xl font-bold ${getFearGreedColor(fearGreed?.value || 50)}`}>
              {fearGreed?.value || "—"}
            </p>
            <p className="text-zinc-500 text-sm mt-1">
              {fearGreed?.classification || "Neutral"}
            </p>
          </motion.div>

          {/* Halving Countdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-dark p-6"
            data-testid="halving-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <Clock className="text-[#F7931A]" size={18} />
              <span className="text-zinc-400 text-sm">Next Halving</span>
            </div>
            <p className="font-mono text-2xl font-bold text-white">
              {halving?.estimated_days?.toLocaleString() || "—"} days
            </p>
            <p className="text-zinc-500 text-sm mt-1">
              {halving?.blocks_remaining?.toLocaleString() || "—"} blocks
            </p>
          </motion.div>

          {/* Mempool Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-dark p-6"
            data-testid="mempool-widget"
          >
            <div className="flex items-center gap-2 mb-3">
              <Zap className="text-[#00E5FF]" size={18} />
              <span className="text-zinc-400 text-sm">Mempool</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Fast fee</span>
                <span className="font-mono text-white">{mempool?.fastest_fee || "—"} sat/vB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Block height</span>
                <span className="font-mono text-white">{mempool?.block_height?.toLocaleString() || "—"}</span>
              </div>
            </div>
          </motion.div>

          {/* Course Progress Card - Wide */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="md:col-span-2 card-dark p-6"
            data-testid="course-progress-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="text-[#F7931A]" size={20} />
                <span className="text-white font-medium">Course Progress</span>
              </div>
              <div className="flex items-center gap-3">
                <ShareCardButton
                  type="course_progress"
                  data={{
                    title: "Course Progress",
                    user_name: user?.first_name || "",
                    completed: completedSections,
                    total_xp: user?.total_lifetime_xp || 0,
                    xp_title: user?.xp_title || "Fiat Sleeper"
                  }}
                  shareText={`I've completed ${completedSections}/21 sections on Bitcoin Brain with ${(user?.total_lifetime_xp || 0).toLocaleString()} BTC XP! Learning Bitcoin the right way.`}
                  shareUrl="https://bitcoinbrain.space"
                />
                <span className="text-[#F7931A] font-mono">{completedSections}/21</span>
              </div>
            </div>
            
            <Progress value={courseProgress} className="h-3 mb-4" />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-500 text-sm">Current Section</p>
                <p className="text-white font-medium">
                  {sections.find(s => s.id === user?.current_section)?.title || "Section 1"}
                </p>
              </div>
              {user?.purchase_tier === "free" && (user?.current_section || 1) > 1 ? (
                <Button 
                  onClick={() => navigate("/pricing")}
                  className="btn-primary"
                  data-testid="continue-course-btn"
                >
                  Upgrade <Crown size={18} />
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate(`/course/section/${user?.current_section || 1}`)}
                  className="btn-primary"
                  data-testid="continue-course-btn"
                >
                  Continue <ChevronRight size={18} />
                </Button>
              )}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="md:col-span-2 card-dark p-6"
            data-testid="quick-actions-card"
          >
            <h3 className="text-white font-medium mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <button
                onClick={() => navigate("/games/quiz-arena")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group"
                data-testid="quick-quiz-btn"
              >
                <Gamepad2 className="text-[#F7931A] mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-[#F7931A]">Quiz Game</p>
              </button>
              <button
                onClick={() => navigate("/timeline")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-purple-500/50 transition-all group"
                data-testid="quick-timeline-btn"
              >
                <Clock className="text-purple-400 mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-purple-400">Timeline</p>
              </button>
              <button
                onClick={() => navigate("/myths")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-yellow-500/50 transition-all group"
                data-testid="quick-myths-btn"
              >
                <ShieldAlert className="text-yellow-400 mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-yellow-400">Myth Busters</p>
              </button>
              <button
                onClick={() => navigate("/rewards")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group"
                data-testid="quick-rewards-btn"
              >
                <Zap className="text-[#F7931A] mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-[#F7931A]">Rewards</p>
              </button>
              <button
                onClick={() => navigate("/kid-mode")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#00E5FF]/50 transition-all group"
                data-testid="quick-kidmode-btn"
              >
                <Sparkles className="text-[#00E5FF] mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-[#00E5FF]">Kid Mode</p>
              </button>
              <button
                onClick={() => navigate("/leaderboard")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group"
                data-testid="quick-leaderboard-btn"
              >
                <Trophy className="text-[#F7931A] mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-[#F7931A]">Leaderboard</p>
              </button>
              <button
                onClick={() => navigate("/journey")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-green-500/50 transition-all group"
                data-testid="quick-journey-btn"
              >
                <TrendingUp className="text-green-400 mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-green-400">Journey</p>
              </button>
              <button
                onClick={() => navigate("/games/simulator")}
                className="p-4 bg-[#161616] rounded-lg border border-white/5 hover:border-[#F7931A]/50 transition-all group"
                data-testid="quick-simulator-btn"
              >
                <Bitcoin className="text-[#F7931A] mb-2 mx-auto" size={24} />
                <p className="text-white text-sm group-hover:text-[#F7931A]">BTC Simulator</p>
              </button>
              {user?.purchase_tier === "educator" && (
                <button
                  onClick={() => navigate("/educator")}
                  className="p-4 bg-[#F7931A]/5 rounded-lg border border-[#F7931A]/20 hover:border-[#F7931A]/60 transition-all group"
                  data-testid="quick-educator-btn"
                >
                  <GraduationCap className="text-[#F7931A] mb-2 mx-auto" size={24} />
                  <p className="text-[#F7931A] text-sm font-medium group-hover:text-white">My Students</p>
                </button>
              )}
            </div>
          </motion.div>

          {/* Explore */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="md:col-span-2 card-dark p-5"
          >
            <h3 className="text-white font-medium mb-3 text-sm">Explore</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { label: "Brief", icon: Newspaper, path: "/weekly-brief", color: "text-blue-400", paid: true },
                { label: "Rabbit Hole", icon: Search, path: "/rabbit-hole", color: "text-purple-400", paid: true },
                { label: "AI Assistant", icon: Brain, path: "/assistant", color: "text-cyan-400" },
                { label: "Trust", icon: Users, path: "/brain-trust", color: "text-green-400" },
                { label: "Alerts", icon: Bell, path: "/price-alerts", color: "text-red-400" },
                { label: "Eggs", icon: Star, path: "/easter-eggs", color: "text-[#F7931A]" },
                { label: "Mission", icon: Heart, path: "/mission", color: "text-pink-400" },
              ].map((item) => {
                const locked = item.paid && user?.purchase_tier === "free";
                return (
                <button
                  key={item.label}
                  onClick={() => locked ? navigate("/pricing") : navigate(item.path)}
                  className="p-2.5 rounded-lg bg-[#0A0A0A] border border-white/5 hover:border-[#F7931A]/40 transition-all text-center group relative"
                  data-testid={`explore-${item.label.toLowerCase().replace(/\s/g, '-')}-btn`}
                >
                  {locked && <Lock className="absolute top-1.5 right-1.5 text-zinc-600" size={10} />}
                  <item.icon className={`${locked ? "text-zinc-600" : item.color} mx-auto mb-1`} size={18} />
                  <p className={`text-xs group-hover:text-white ${locked ? "text-zinc-600" : "text-zinc-500"}`}>{item.label}</p>
                </button>
              )})}
            </div>
          </motion.div>

          {/* Referral */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.68 }}
            className="md:col-span-2 card-dark p-5 border-emerald-500/20 cursor-pointer hover:border-emerald-500/40 transition-all"
            onClick={() => navigate("/referral")}
            data-testid="referral-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Share2 className="text-emerald-400" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Refer a Friend</p>
                  <p className="text-zinc-500 text-xs">Share Bitcoin Brain and earn BTC XP together</p>
                </div>
              </div>
              <ChevronRight className="text-zinc-600" size={20} />
            </div>
          </motion.div>

          {/* Achievements Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="md:col-span-2 card-dark p-6"
            data-testid="achievements-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="text-[#F7931A]" size={20} />
                <span className="text-white font-medium">Achievements</span>
              </div>
              <Link to="/achievements" className="text-[#F7931A] text-sm hover:underline">
                View all
              </Link>
            </div>
            
            <div className="flex gap-3 overflow-x-auto pb-2">
              {(user?.badges || []).length > 0 ? (
                user.badges.map((badge) => (
                  <div key={badge} className="flex-shrink-0 p-3 bg-[#F7931A]/10 rounded-lg border border-[#F7931A]/30">
                    <Trophy className="text-[#F7931A] mx-auto mb-1" size={20} />
                    <p className="text-white text-xs capitalize">{badge.replace(/_/g, ' ')}</p>
                  </div>
                ))
              ) : (
                <div className="text-zinc-500 text-sm">
                  Complete lessons to earn badges!
                </div>
              )}
            </div>
          </motion.div>

          {/* Bitcoin Brain Score */}
          {brainScore && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75 }}
              className="md:col-span-2 card-dark p-6 cursor-pointer hover:border-[#F7931A]/50"
              onClick={() => navigate("/rewards")}
              data-testid="brain-score-widget"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                    <Target className="text-[#F7931A]" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Bitcoin Brain Score</p>
                    <p className="text-zinc-500 text-xs">{brainScore.label}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-mono text-3xl font-bold gradient-text">{brainScore.score}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Upgrade CTA — free users */}
          {user?.purchase_tier === "free" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="md:col-span-2 card-dark p-6 border-[#F7931A]/30 bg-gradient-to-br from-[#F7931A]/10 to-transparent"
              data-testid="upgrade-cta-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-heading text-xl font-bold">
                    Get Bitcoin Brain
                  </h3>
                  <p className="text-zinc-400 text-sm mt-1">
                    Pay once. Own forever. All 21 sections, all quiz levels, AI assistant.
                  </p>
                </div>
                <Button 
                  onClick={() => navigate("/pricing")}
                  className="btn-primary"
                  data-testid="upgrade-btn"
                >
                  Get Access <ChevronRight size={18} />
                </Button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-white/5" data-testid="dashboard-footer">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-500">
            <a href="https://www.youtube.com/@BitcoinBrainApp" target="_blank" rel="noopener noreferrer" className="hover:text-[#F7931A] transition-colors" data-testid="footer-youtube">
              <Youtube size={20} />
            </a>
            <Link to="/mission" className="hover:text-[#F7931A] transition-colors" data-testid="footer-mission">Mission</Link>
            <Link to="/pricing" className="hover:text-[#F7931A] transition-colors" data-testid="footer-pricing">Pricing</Link>
            <Link to="/legal/privacy" className="hover:text-[#F7931A] transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-[#F7931A] transition-colors" data-testid="footer-terms">Terms</Link>
          </div>
          <p className="text-center text-zinc-700 text-xs mt-4 pb-8">&copy; {new Date().getFullYear()} Bitcoin Brain</p>
        </footer>
      </main>
    </div>
  );
}
